import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from "@tanstack/react-query";
import { fetchDefaultFilterData } from "../../services/defaultFilters.service";
import { updateUserSettings } from "./services/updateUserSettings.service";
import { IPreferredFilter } from "./interfaces/userSettingsFilters.interface";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSortAlphaDown, FaSortAlphaUp } from "react-icons/fa";
import { Tooltip } from "react-tooltip";
import { IUser } from "../../../../context/interfaces/User";
import { fetchUserSettings } from "./services/fetchUserSettings.service";
import { SearchTableInput } from "../SearchTableInput/SearchTableInput";
import { fetchRegulatorsByCountry } from "./services/fetchRegulatorsByCountry.service";

interface UserSettingsTabProps {
  user: IUser;
  defaultFilters: {
    country?: string[];
    regulator?: string[];
    class_name?: string[];
    classes?: string[];
  };
  setUser: (user: IUser) => void;
}

type LSSettingsFilter = {
  country: string[];
  regulator: string[];
  classes: string[];
  filtersExpanded: boolean;
};

const readLS = (): LSSettingsFilter => {
  try {
    const settingsLSData = localStorage.getItem("settings");
    if (settingsLSData) {
      const parsed = JSON.parse(settingsLSData);
      return {
        country: Array.isArray(parsed?.country) ? parsed.country : [],
        regulator: Array.isArray(parsed?.regulator) ? parsed.regulator : [],
        classes: Array.isArray(parsed?.classes) ? parsed.classes : [],
        filtersExpanded: Boolean(parsed?.filtersExpanded),
      };
    }
  } catch {}
  return { country: [], regulator: [], classes: [], filtersExpanded: false };
};

const sortArray = (arr: string[], asc: boolean) =>
  [...arr].sort((a, b) => (asc ? a.localeCompare(b) : b.localeCompare(a)));

const UserSettingsTab: React.FC<UserSettingsTabProps> = ({
  user,
  setUser,
  defaultFilters,
}) => {
  const { t } = useTranslation();
  const [baseSettings, setBaseSettings] = useState<LSSettingsFilter>(() => readLS());

  const [selectedCountries, setSelectedCountries] = useState<string[]>(baseSettings.country);
  const [selectedRegulators, setSelectedRegulators] = useState<string[]>(baseSettings.regulator);
  const [selectedClasses, setSelectedClasses] = useState<string[]>(baseSettings.classes);
  const [filtersExpanded, setFiltersExpanded] = useState<boolean>(baseSettings.filtersExpanded);

  const [allClasses, setAllClasses] = useState<string[]>([]);
  const [allRegulators, setAllRegulators] = useState<string[]>(
    defaultFilters.regulator || [],
  );

  const [countrySortAsc, setCountrySortAsc] = useState(true);
  const [regulatorSortAsc, setRegulatorSortAsc] = useState(true);
  const [classSortAsc, setClassSortAsc] = useState(true);

  // ✅ local search per section (optional but very useful for large lists)
  const [countrySearch, setCountrySearch] = useState("");
  const [regSearch, setRegSearch] = useState("");
  const [classSearch, setClassSearch] = useState("");

  const TOASTER_ID = "update-user-settings-toaster";

  const bodyRef = React.useRef<HTMLDivElement | null>(null);
  const scrollPosRef = React.useRef<number | null>(null);
  const selectedCountriesRef = React.useRef<string[]>(selectedCountries);
  const selectedRegulatorsRef = React.useRef<string[]>(selectedRegulators);
  const selectedClassesRef = React.useRef<string[]>(selectedClasses);
  const filtersExpandedRef = React.useRef<boolean>(filtersExpanded);
  const baseSettingsRef = React.useRef<LSSettingsFilter>(baseSettings);

  const shouldFetch =
    selectedCountries.length > 0 || selectedRegulators.length > 0;

  const { data: defaultFilterData } = useQuery({
    queryKey: ["defaultFilterData", selectedCountries, selectedRegulators],
    queryFn: () =>
      fetchDefaultFilterData({
        country: selectedCountries,
        regulator: selectedRegulators,
      }),
    enabled: shouldFetch,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const { data: regsForCountries } = useQuery({
    queryKey: ["defaultFilterRegulators", selectedCountries],
    queryFn: () => fetchDefaultFilterData({ country: selectedCountries }),
    enabled: selectedCountries.length > 0,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const { data: userSettingsData, isSuccess, isFetchedAfterMount } = useQuery({
    queryKey: ["userSettingsData"],
    queryFn: () => fetchUserSettings(),
    refetchOnWindowFocus: false,
    // Ensure we don't reuse stale cached settings when returning to the tab
    // after navigating away.
    refetchOnMount: true,
    refetchOnReconnect: false,
  });

  const { data: regulatorsByCountry } = useQuery({
    queryKey: ["regulatorsByCountry"],
    queryFn: () => fetchRegulatorsByCountry(),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const arraysEqual = (a: string[], b: string[]) => {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((value, index) => value === sortedB[index]);
  };

  const hasChanges =
    !arraysEqual(selectedCountries, baseSettings.country) ||
    !arraysEqual(selectedRegulators, baseSettings.regulator) ||
    !arraysEqual(selectedClasses, baseSettings.classes) ||
    filtersExpanded !== baseSettings.filtersExpanded;

  const { mutate: mutateUserSettings } = useMutation({
    mutationFn: async (prefFilter: IPreferredFilter) =>
      updateUserSettings(prefFilter),
    onSuccess: () => {
      if (!hasChanges) return;

      setUser({
        ...user,
        settings: {
          country: selectedCountries,
          regulator: selectedRegulators,
          classes: selectedClasses,
        },
      });

      toast.dismiss(TOASTER_ID);
      toast.success(t("userSettings.saveChangesToaster"), { toastId: TOASTER_ID });

      localStorage.setItem(
        "settings",
        JSON.stringify({
          country: selectedCountries,
          regulator: selectedRegulators,
          classes: selectedClasses,
          filtersExpanded,
        }),
      );

      const saved = {
        country: selectedCountries,
        regulator: selectedRegulators,
        classes: selectedClasses,
        filtersExpanded,
      };
      setBaseSettings(saved);
      window.dispatchEvent(new Event("settings:updated"));
    },
  });

  useEffect(() => {
    if (!shouldFetch) {
    }
  }, [shouldFetch]);

  useEffect(() => {
    const globalClasses = defaultFilters.classes || defaultFilters.class_name || [];

    if (selectedRegulators.length > 0) {
      if (defaultFilterData?.classes) {
        if (JSON.stringify(allClasses) !== JSON.stringify(defaultFilterData.classes)) {
          setAllClasses(defaultFilterData.classes);
        }
      } else if (JSON.stringify(allClasses) !== JSON.stringify(globalClasses)) {
        setAllClasses(globalClasses);
      }
    } else {
      if (allClasses.length !== 0) setAllClasses([]);
      if (selectedClasses.length !== 0) setSelectedClasses([]);
    }

    if (selectedCountries.length === 0) {
      setAllRegulators(defaultFilters.regulator || []);
    }
  }, [selectedCountries, selectedRegulators, defaultFilterData, defaultFilters.regulator, defaultFilters.classes, defaultFilters.class_name]);

  useEffect(() => {
    if (allClasses.length > 0) {
      const filtered = selectedClasses.filter((c) => allClasses.includes(c));
      if (JSON.stringify(filtered) !== JSON.stringify(selectedClasses)) {
        setSelectedClasses(filtered);
      }
    }
  }, [allClasses]);

  // restore preserved scroll position after selection changes to avoid jumping
  useEffect(() => {
    if (scrollPosRef.current == null) return;
    const pos = scrollPosRef.current;
    requestAnimationFrame(() => {
      try {
        if (bodyRef.current) bodyRef.current.scrollTop = pos as number;
      } catch (e) {
        // ignore
      }
      scrollPosRef.current = null;
    });
  }, [selectedCountries, selectedRegulators, selectedClasses]);

  useEffect(() => {
    const filtered = selectedRegulators.filter((r) =>
      allRegulators.includes(r),
    );
    if (JSON.stringify(filtered) !== JSON.stringify(selectedRegulators)) {
      setSelectedRegulators(filtered);
    }
  }, [allRegulators]);

  useEffect(() => {
    if (!userSettingsData) return;
    if (!isSuccess) return;

    const saved = {
      country: Array.isArray(userSettingsData.country) ? userSettingsData.country : [],
      regulator: Array.isArray(userSettingsData.regulator) ? userSettingsData.regulator : [],
      classes: Array.isArray(userSettingsData.classes) ? userSettingsData.classes : [],
      filtersExpanded: readLS().filtersExpanded,
    };

    localStorage.setItem("settings", JSON.stringify(saved));
    window.dispatchEvent(new Event("settings:updated"));

    setBaseSettings(saved);
    setSelectedCountries(saved.country);
    setSelectedRegulators(saved.regulator);
    setSelectedClasses(saved.classes);
    setFiltersExpanded(saved.filtersExpanded);
  }, [isSuccess, isFetchedAfterMount, userSettingsData]);

  useEffect(() => {
    selectedCountriesRef.current = selectedCountries;
    selectedRegulatorsRef.current = selectedRegulators;
    selectedClassesRef.current = selectedClasses;
    filtersExpandedRef.current = filtersExpanded;
    baseSettingsRef.current = baseSettings;

    const syncFromLocalStorage = () => {
      const cur = readLS();

      // don't clobber user's unsaved edits — only sync when there are no local changes
      const localHasChanges =
        !arraysEqual(selectedCountriesRef.current, baseSettingsRef.current.country) ||
        !arraysEqual(selectedRegulatorsRef.current, baseSettingsRef.current.regulator) ||
        !arraysEqual(selectedClassesRef.current, baseSettingsRef.current.classes) ||
        filtersExpandedRef.current !== baseSettingsRef.current.filtersExpanded;

      if (!localHasChanges) {
        setBaseSettings(cur);
        setFiltersExpanded(Boolean(cur.filtersExpanded));
        setSelectedCountries(cur.country || []);
        setSelectedRegulators(cur.regulator || []);
        setSelectedClasses(cur.classes || []);
      }
    };

    window.addEventListener("settings:updated", syncFromLocalStorage as EventListener);
    window.addEventListener("storage", syncFromLocalStorage);
    // when the tab becomes visible in the admin panel, force-sync from localStorage
    const forceSync = () => {
      const cur = readLS();
      setBaseSettings(cur);
      setFiltersExpanded(Boolean(cur.filtersExpanded));
      setSelectedCountries(cur.country || []);
      setSelectedRegulators(cur.regulator || []);
      setSelectedClasses(cur.classes || []);
    };
    window.addEventListener("userSettings:show", forceSync as EventListener);
    return () => {
      window.removeEventListener("settings:updated", syncFromLocalStorage as EventListener);
      window.removeEventListener("storage", syncFromLocalStorage);
      window.removeEventListener("userSettings:show", forceSync as EventListener);
    };
  }, [selectedCountries, selectedRegulators, selectedClasses, filtersExpanded, baseSettings]);

  useEffect(() => {
    if (regsForCountries && Array.isArray(regsForCountries.regulator)) {
      setAllRegulators(regsForCountries.regulator);
      setSelectedRegulators((prev) => prev.filter((r) => regsForCountries.regulator.includes(r)));
      return;
    }

    if (defaultFilterData?.regulator) {
      setAllRegulators(defaultFilterData.regulator);
      setSelectedRegulators((prev) => prev.filter((r) => defaultFilterData.regulator.includes(r)));
      return;
    }

    setAllRegulators(defaultFilters.regulator || []);
  }, [regsForCountries, defaultFilterData, defaultFilters.regulator]);

  const handleCountryChange = (country: string) => {
    // preserve scroll position to avoid jumping to the top
    scrollPosRef.current = bodyRef.current?.scrollTop ?? null;
    setSelectedCountries((prev) =>
      prev.includes(country) ? prev.filter((c) => c !== country) : [...prev, country],
    );
  };

  const handleRegulatorChange = (reg: string) => {
    scrollPosRef.current = bodyRef.current?.scrollTop ?? null;
    setSelectedRegulators((prev) =>
      prev.includes(reg) ? prev.filter((r) => r !== reg) : [...prev, reg],
    );
  };

  const handleClassChange = (cl: string) => {
    scrollPosRef.current = bodyRef.current?.scrollTop ?? null;
    setSelectedClasses((prev) =>
      prev.includes(cl) ? prev.filter((c) => c !== cl) : [...prev, cl],
    );
  };

  // Derive regulator codes from selected regulator names
  const selectedRegulatorCodes = useMemo(() => {
    if (!regulatorsByCountry || selectedRegulators.length === 0) return [];
    
    const codes: string[] = [];
    regulatorsByCountry.forEach((countryData) => {
      countryData.regulators.forEach((regulator) => {
        if (selectedRegulators.includes(regulator.name)) {
          codes.push(regulator.code);
        }
      });
    });
    return codes;
  }, [regulatorsByCountry, selectedRegulators]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    mutateUserSettings({
      country: selectedCountries,
      regulator: selectedRegulators,
      code: selectedRegulatorCodes,
      classes: selectedClasses,
    });
  };

  const resetChanges = () => {
    const cur = readLS();
    setSelectedCountries(cur.country || []);
    setSelectedRegulators(cur.regulator || []);
    setSelectedClasses(cur.classes || []);
    setFiltersExpanded(Boolean(cur.filtersExpanded));
    toast.info(t("userSettings.discardChangesToaster"), { toastId: "reset-toast" });
  };

  const resetToDefaults = () => {
    setSelectedCountries([]);
    setSelectedRegulators([]);
    setSelectedClasses([]);
    setFiltersExpanded(false);
    // don't overwrite saved baseSettings yet -- user should click Save to persist
  };

  const persistLocalSettings = (nextExpanded: boolean) => {
    localStorage.setItem(
      "settings",
      JSON.stringify({
        country: selectedCountries,
        regulator: selectedRegulators,
        classes: selectedClasses,
        filtersExpanded: nextExpanded,
      }),
    );
    window.dispatchEvent(new Event("settings:updated"));
  };

  // derived sorted lists
  const sortedCountries = useMemo(
    () => sortArray(defaultFilters.country || [], countrySortAsc),
    [defaultFilters.country, countrySortAsc],
  );
  const sortedRegulators = useMemo(
    () => sortArray(allRegulators || [], regulatorSortAsc),
    [allRegulators, regulatorSortAsc],
  );
  const sortedClasses = useMemo(
    () => sortArray(allClasses || [], classSortAsc),
    [allClasses, classSortAsc],
  );

  // section filtering
  const visibleCountries = useMemo(() => {
    const q = countrySearch.trim().toLowerCase();
    if (!q) return sortedCountries;
    return sortedCountries.filter((x) => x.toLowerCase().includes(q));
  }, [sortedCountries, countrySearch]);

  const visibleRegs = useMemo(() => {
    const q = regSearch.trim().toLowerCase();
    if (!q) return sortedRegulators;
    return sortedRegulators.filter((x) => x.toLowerCase().includes(q));
  }, [sortedRegulators, regSearch]);

  const visibleClasses = useMemo(() => {
    const q = classSearch.trim().toLowerCase();
    if (!q) return sortedClasses;
    return sortedClasses.filter((x) => x.toLowerCase().includes(q));
  }, [sortedClasses, classSearch]);

  const btnBase =
    "inline-flex items-center justify-center h-10 px-4 rounded-lg text-sm font-medium transition";
  const btnGreen = `${btnBase} bg-del-green text-white hover:opacity-90`;
  const btnWhite =
    `${btnBase} bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 ` +
    `text-slate-900 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800`;
  const btnDisabled =
    `${btnBase} bg-slate-200 dark:bg-slate-700 text-slate-400 dark:text-slate-400 cursor-not-allowed`;

  const SectionCard: React.FC<{
    title: string;
    count: number;
    sortAsc: boolean;
    onToggleSort: () => void;
    tooltipClass: string;
    searchValue: string;
    onSearchChange: (v: string) => void;
    children: React.ReactNode;
    disabled?: boolean;
  }> = ({
    title,
    count,
    sortAsc,
    onToggleSort,
    tooltipClass,
    searchValue,
    onSearchChange,
    children,
    disabled,
  }) => (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm">
      <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
              {title}
            </h4>
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {count}
            </span>
          </div>
        </div>

        <button
          type="button"
          disabled={!!disabled}
          className={[
            "h-9 w-9 inline-flex items-center justify-center rounded-lg transition",
            disabled
              ? "opacity-40 cursor-not-allowed"
              : "hover:bg-slate-100 dark:hover:bg-slate-800",
            tooltipClass,
          ].join(" ")}
          onClick={onToggleSort}
          aria-label="Toggle sort"
        >
          {sortAsc ? (
            <FaSortAlphaDown className="text-del-green" />
          ) : (
            <FaSortAlphaUp className="text-del-green" />
          )}
        </button>
      </div>

      {!disabled && (
        <div className="px-4 py-3">
          <div className="max-w-md w-full">
            <SearchTableInput
              onInputChange={(v) => onSearchChange(v)}
              placeholderText={t("adminPanel.search", { defaultValue: "Search..." })}
            />
          </div>
        </div>
      )}

      <div className="px-2 pb-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-2">
          {children}
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden">
      {/* Top header (match other admin tabs) */}
      <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-6 py-5 sticky top-0 z-10">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            {t("userSettings.preferredFilters")}
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {t("userSettings.filtersInfoText")}
          </p>
        </div>
      </div>

      {/* Content area */}
      <div className="flex-1 min-h-0 bg-slate-100 dark:bg-slate-800/40 p-6 overflow-hidden">
        <div className="h-full min-h-0 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden flex flex-col">
          <form onSubmit={handleSubmit} className="flex flex-col h-full min-h-0 overflow-hidden">
            {/* Scrollable body */}
            <div ref={bodyRef} className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      {t("userSettings.filtersDefaultState", {
                        defaultValue: "Filters default state",
                      })}
                    </h4>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                      {t("userSettings.filtersDefaultStateHint", {
                        defaultValue:
                          "Choose whether the filters panel opens expanded or collapsed on the Home page.",
                      })}
                    </p>
                  </div>

                  <div className="inline-flex items-center gap-3">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={filtersExpanded}
                      onClick={() => {
                        const next = !filtersExpanded;
                        setFiltersExpanded(next);
                        persistLocalSettings(next);
                      }}
                      className={
                        [
                          "relative inline-flex h-5 w-10 items-center rounded-full transition-colors",
                          filtersExpanded ? "bg-del-green" : "bg-slate-300 dark:bg-slate-600",
                        ].join(" ")
                      }
                      aria-label="Toggle filters default state"
                    >
                      <span
                        className={
                          [
                            "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                            filtersExpanded ? "translate-x-5" : "translate-x-1",
                          ].join(" ")
                        }
                      />
                    </button>
                    <span className="text-sm text-slate-700 dark:text-slate-200">
                      {filtersExpanded
                        ? t("userSettings.filtersExpanded", {
                            defaultValue: "Expanded",
                          })
                        : t("userSettings.filtersCollapsed", {
                            defaultValue: "Collapsed",
                          })}
                    </span>
                  </div>
                </div>
              </div>

              <SectionCard
                title={`${t("userSettings.selectCountry")}`}
                count={selectedCountries.length}
                sortAsc={countrySortAsc}
                onToggleSort={() => setCountrySortAsc((a) => !a)}
                tooltipClass="tooltip-sort-country"
                searchValue={countrySearch}
                onSearchChange={(v) => setCountrySearch(v.toLowerCase())}
              >
                {visibleCountries.map((country) => (
                  <label
                    key={country}
                    className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200 px-2 py-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition"
                  >
                    <input
                      type="checkbox"
                      value={country}
                      checked={selectedCountries.includes(country)}
                      onChange={() => handleCountryChange(country)}
                      className="mt-0.5 accent-del-light-theme-green dark:accent-del-green"
                    />
                    <span className="min-w-0 break-words">{country}</span>
                  </label>
                ))}
              </SectionCard>

              <SectionCard
                title={`${t("userSettings.selectRegulator")}`}
                count={selectedRegulators.length}
                sortAsc={regulatorSortAsc}
                onToggleSort={() => setRegulatorSortAsc((a) => !a)}
                tooltipClass="tooltip-sort-regulator"
                searchValue={regSearch}
                onSearchChange={(v) => setRegSearch(v.toLowerCase())}
                disabled={selectedCountries.length === 0}
              >
                {selectedCountries.length === 0 ? (
                  <div className="px-2 py-2 text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap max-w-[420px]">
                    {t("userSettings.selectCountryFirst", {
                      defaultValue: "Select one or more countries to choose regulators",
                    })}
                  </div>
                ) : (
                  visibleRegs.map((reg) => (
                    <label
                      key={reg}
                      className={`flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200 px-2 py-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition ${selectedCountries.length === 0 ? "opacity-40 cursor-not-allowed" : ""}`}
                    >
                      <input
                        type="checkbox"
                        value={reg}
                        checked={selectedRegulators.includes(reg)}
                        onChange={() => handleRegulatorChange(reg)}
                        disabled={selectedCountries.length === 0}
                        className="mt-0.5 accent-del-light-theme-green dark:accent-del-green"
                      />
                      <span className="min-w-0 break-words">{reg}</span>
                    </label>
                  ))
                )}
              </SectionCard>

              <SectionCard
                title={`${t("userSettings.selectClass")}`}
                count={selectedClasses.length}
                sortAsc={classSortAsc}
                onToggleSort={() => setClassSortAsc((a) => !a)}
                tooltipClass="tooltip-sort-class"
                searchValue={classSearch}
                onSearchChange={(v) => setClassSearch(v.toLowerCase())}
                disabled={selectedRegulators.length === 0 || sortedClasses.length === 0}
              >
                {selectedCountries.length === 0 ? (
                  <div className="px-2 py-2 text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap max-w-[420px]">
                    {t("userSettings.selectCountryFirst", {
                      defaultValue: "Select one or more countries to choose regulators",
                    })}
                  </div>
                ) : selectedRegulators.length === 0 ? (
                  <div className="px-2 py-2 text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap max-w-[420px]">
                    {t("userSettings.selectRegulatorFirst", {
                      defaultValue: "Select one or more regulators to choose classes",
                    })}
                  </div>
                ) : sortedClasses.length === 0 ? (
                  <div className="px-2 py-2 text-sm text-slate-500 dark:text-slate-400">
                    {t("userSettings.noClass")}
                  </div>
                ) : (
                  visibleClasses.map((cl) => (
                    <label
                      key={cl}
                      className={`flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200 px-2 py-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition ${selectedRegulators.length === 0 ? "opacity-40 cursor-not-allowed" : ""}`}
                    >
                      <input
                        type="checkbox"
                        value={cl}
                        checked={selectedClasses.includes(cl)}
                        onChange={() => handleClassChange(cl)}
                        disabled={selectedRegulators.length === 0}
                        className="mt-0.5 accent-del-light-theme-green dark:accent-del-green"
                      />
                      <span className="min-w-0 break-words">{cl}</span>
                    </label>
                  ))
                )}
              </SectionCard>
            </div>

            <div className="shrink-0 border-t border-slate-200 dark:border-slate-700 px-4 py-3 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={resetToDefaults}
                className={btnWhite}
              >
                {t("userSettings.resetDefaults", { defaultValue: "Reset to defaults" })}
              </button>

              {hasChanges && (
                <button
                  type="button"
                  onClick={resetChanges}
                  className={btnWhite}
                >
                  {t("userSettings.discardChangesBtn")}
                </button>
              )}

              <button
                type="submit"
                disabled={!hasChanges}
                className={hasChanges ? btnGreen : btnDisabled}
              >
                {t("adminPanel.save")}
              </button>
            </div>
          </form>
        </div>

        {/* Tooltips */}
        <Tooltip
          anchorSelect=".tooltip-sort-country"
          place="top"
          className="text-xs font-light z-50"
          content={countrySortAsc ? t("adminPanel.sortAz") : t("adminPanel.sortZa")}
        />
        <Tooltip
          anchorSelect=".tooltip-sort-regulator"
          place="top"
          className="text-xs font-light z-50"
          content={regulatorSortAsc ? t("adminPanel.sortAz") : t("adminPanel.sortZa")}
        />
        <Tooltip
          anchorSelect=".tooltip-sort-class"
          place="top"
          className="text-xs font-light z-50"
          content={classSortAsc ? t("adminPanel.sortAz") : t("adminPanel.sortZa")}
        />
      </div>
    </div>
  );
};

export { UserSettingsTab };